/* TenantWordmark — header/footer brand element for tenant public templates.

   Renders the studio's uploaded logo image when available, with a
   bespoke text wordmark as the fallback (`children`).

   Logo resolution order:
     1. `tone="dark"` + a `coverLogoUrl` set → use the cover/inverted lockup
        (white-on-dark variant authored for use over photo heroes and dark
        chrome bands). This is the per-tone swap that lets a studio look
        crisp on Bold's stone-950 masthead AND on Atelier's cream salon.
     2. `logoUrl` set                       → use the main logo.
     3. Neither set                          → render `children` (the
        template's bespoke text wordmark — italic serif for Atelier,
        JetBrains brackets for Brutalist, "The ___ Journal" for Journal).

   Each template Layout passes its own `imgClassName` so the logo height
   tracks the visual weight of the text it replaces (h-7 for thin magazine
   bars, h-10–h-14 for salon mastheads, growing/shrinking on scroll for
   Spotlight + Atelier). */
import React from "react";

export default function TenantWordmark({
  logoUrl,
  coverLogoUrl,
  studioName,
  tone = "light",     // "light" (default) or "dark" — picks main vs cover
  children,
  imgClassName = "h-8 w-auto",
  testid = "tenant-wordmark-img",
}) {
  const main = typeof logoUrl === "string" ? logoUrl.trim() : "";
  const cover = typeof coverLogoUrl === "string" ? coverLogoUrl.trim() : "";

  // Prefer the cover lockup on dark surfaces when it's actually been
  // authored; fall back to the main logo if the studio only uploaded one.
  const chosen = tone === "dark" && cover ? cover : (main || cover);

  if (chosen) {
    return (
      <img
        src={chosen}
        alt={studioName || "Studio"}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        draggable={false}
        data-testid={testid}
        className={`${imgClassName} object-contain object-left select-none max-w-[160px] sm:max-w-[220px] lg:max-w-[260px]`}
      />
    );
  }
  return children ?? <span>{studioName}</span>;
}
